import { Alert, Box, Button, CircularProgress, Typography } from '@mui/material';
import { useState } from 'react';
import CardCredit from '~/components/CardCredit';
import { StandardJourneyInput } from '~/components/StandardJourney';
import JourneyAddressFields from '~/components/StandardJourney/JourneyAddressFields';
import { useJourneyAddressForm, type JourneyZipcodeLookup } from '~/components/StandardJourney/useJourneyAddressForm';
import {
  CardTokenizationError,
  tokenizeCardWithPagarme,
  type CardField,
  type CardInput,
  type CardToken
} from '~/services/modules/paymentGateway';
import type { BrazilAddressFields } from '~/utils/brazilAddress';
import { isCardExpiryValid, isCardNumberValid } from '~/utils/creditCard';
import type { JourneyAddedCard } from './types';

type Props = {
  appId: string;
  /** Chave pública do gateway da organização (`usePaymentGatewayPublicKey`). */
  publicKey: string | null | undefined;
  publicKeyLoading?: boolean;
  /** Pré-preenche o endereço de cobrança (endereço do contrato). */
  defaultBillingAddress: Partial<BrazilAddressFields>;
  lookupZipcode: JourneyZipcodeLookup;
  onAdded: (card: JourneyAddedCard) => void;
  /** Injetável para stories/testes; o padrão tokeniza no Pagar.me. */
  tokenize?: (publicKey: string | null | undefined, card: CardInput) => Promise<CardToken>;
  /**
   * `false` esconde e não valida o «Endereço de cobrança» (troca de cartão: o `PATCH …/card` não recebe endereço).
   * O cartão devolvido leva o `defaultBillingAddress` como está.
   */
  showBillingAddress?: boolean;
};

type CardForm = { number: string; holderName: string; expiry: string; cvv: string };
type CardErrors = Partial<Record<CardField, string>>;

const MISSING_KEY_MESSAGE =
  'Pagamento indisponível: a chave do meio de pagamento desta organização não foi encontrada. Fale com o atendimento.';

function validateCard(form: CardForm): CardErrors {
  const errors: CardErrors = {};
  if (!isCardNumberValid(form.number)) errors.number = 'Confira o número do cartão.';
  if (form.holderName.trim().split(/\s+/).filter(Boolean).length < 2) {
    errors.name = 'Informe o nome como está impresso no cartão.';
  }
  if (!isCardExpiryValid(form.expiry)) errors.expiry = 'Validade inválida ou vencida.';
  if (!/^\d{3,4}$/.test(form.cvv)) errors.cvv = 'Informe o código de segurança (3 ou 4 dígitos).';
  return errors;
}

/**
 * «Dados do Cartão» + «Endereço de cobrança» (Figma 2091:4107, 2091:4592). «Adicionar cartão» valida o cartão
 * no gateway (tokeniza no navegador) para a recusa aparecer aqui; o token usado no pagamento é gerado de novo na
 * hora de pagar. Os dados do cartão nunca vão para a API Aride, só o token.
 */
export default function JourneyNewCardForm({
  appId,
  publicKey,
  publicKeyLoading = false,
  defaultBillingAddress,
  lookupZipcode,
  onAdded,
  tokenize = tokenizeCardWithPagarme,
  showBillingAddress = true
}: Props) {
  const [card, setCard] = useState<CardForm>({ number: '', holderName: '', expiry: '', cvv: '' });
  const [cardErrors, setCardErrors] = useState<CardErrors>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const billing = useJourneyAddressForm(defaultBillingAddress, lookupZipcode);

  const missingKey = !publicKey && !publicKeyLoading;

  const change = (field: keyof CardForm, errorField: CardField) => (value: string) => {
    setCard((current) => ({ ...current, [field]: value }));
    setCardErrors((current) => ({ ...current, [errorField]: undefined }));
    setFormError(null);
  };

  const add = async () => {
    const found = validateCard(card);
    setCardErrors(found);
    const billingOk = showBillingAddress ? billing.validate() : true;
    if (Object.keys(found).length > 0 || !billingOk || missingKey) return;
    setAdding(true);
    setFormError(null);
    try {
      const token = await tokenize(publicKey, card);
      onAdded({
        id: `new-${token.token}`,
        brand: token.brand,
        lastFour: token.lastFour,
        cardInput: { ...card },
        billingAddress: billing.form
      });
    } catch (error: unknown) {
      if (error instanceof CardTokenizationError && error.field) {
        const { field, message } = error;
        setCardErrors((current) => ({ ...current, [field]: message }));
      } else {
        setFormError(
          error instanceof Error && error.message
            ? error.message
            : 'Não foi possível validar o cartão agora. Tente novamente.'
        );
      }
    } finally {
      setAdding(false);
    }
  };

  const input = (
    field: keyof CardForm,
    errorField: CardField,
    label: string,
    extra: { mask?: 'cardNumber' | 'cardExpiry'; placeholder?: string; sanitize?: (v: string) => string } = {}
  ) => (
    <StandardJourneyInput
      appId={`${appId}-${field}`}
      error={Boolean(cardErrors[errorField])}
      errorMessage={cardErrors[errorField]}
      label={label}
      mask={extra.mask}
      onChange={(value) => change(field, errorField)(extra.sanitize ? extra.sanitize(value) : value)}
      placeholder={extra.placeholder}
      value={card[field]}
    />
  );

  return (
    <Box
      component="section"
      data-app-id={appId}
      sx={(theme) => ({
        bgcolor: 'background.paper',
        borderRadius: '16px',
        boxShadow: `0 4px 4px ${theme.customColors.elevation}`,
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
        p: { sm: '24px', xs: '16px' }
      })}
    >
      <Typography component="h3" variant="h6">
        Dados do Cartão
      </Typography>

      {missingKey ? (
        <Alert data-app-id={`${appId}-missing-key`} severity="error">
          {MISSING_KEY_MESSAGE}
        </Alert>
      ) : null}
      {formError ? (
        <Alert data-app-id={`${appId}-error`} severity="error">
          {formError}
        </Alert>
      ) : null}

      <Box sx={{ display: 'flex', justifyContent: 'center', my: 1 }}>
        <CardCredit cvc={card.cvv} expiry={card.expiry} focused="number" name={card.holderName} number={card.number} />
      </Box>

      {input('number', 'number', 'Número do cartão', { mask: 'cardNumber' })}
      {input('holderName', 'name', 'Nome impresso no cartão')}
      <Box sx={{ display: 'grid', gap: '8px', gridTemplateColumns: '1fr 1fr' }}>
        {input('expiry', 'expiry', 'Validade', { mask: 'cardExpiry', placeholder: 'MM/AA' })}
        {input('cvv', 'cvv', 'Código de segurança (CVV)', { sanitize: (v) => v.replace(/\D/g, '').slice(0, 4) })}
      </Box>

      {showBillingAddress ? (
        <>
          <Typography component="h3" sx={{ mt: 2 }} variant="h6">
            Endereço de cobrança
          </Typography>
          <JourneyAddressFields
            appId={`${appId}-billing`}
            errors={billing.errors}
            form={billing.form}
            layout="wide"
            onChange={billing.change}
            onZipcodeChange={billing.changeZipcode}
          />
        </>
      ) : null}

      <Button
        data-app-id={`${appId}-add`}
        disabled={adding || missingKey || publicKeyLoading}
        fullWidth
        onClick={add}
        size="large"
        startIcon={adding ? <CircularProgress aria-hidden color="inherit" size={18} /> : undefined}
        sx={{ borderRadius: '24px', mt: 2 }}
        variant="contained"
      >
        {adding ? 'Adicionando cartão' : 'Adicionar cartão'}
      </Button>
    </Box>
  );
}
